// Guia vivo do Design System — referência visual de todos os tokens e componentes.
// Rota dev (#/design). Regras de uso: docs/DESIGN_SYSTEM.md

import { toast } from '@ui/components/Toast';

const COLORS = [
  { name: '--cyan', hex: '#00f0ff', use: 'primária · ações · foco' },
  { name: '--purple', hex: '#b14aff', use: 'secundária · gradientes' },
  { name: '--pink', hex: '#ff2bd6', use: 'destaque · "agora" · alerta suave' },
  { name: '--green', hex: '#00ff9d', use: 'sucesso · concluído' },
  { name: '--amber', hex: '#ffb000', use: 'atenção · atrasado' },
  { name: '--red', hex: '#ff3860', use: 'erro · destrutivo' },
  { name: '--gold', hex: '#ffd700', use: 'premium · rotina · dev' },
];

const TEXT_COLORS = [
  { name: '--text', use: 'texto principal' },
  { name: '--text-soft', use: 'texto secundário' },
  { name: '--text-dim', use: 'legendas · labels' },
  { name: '--text-mute', use: 'desabilitado · decorativo' },
];

const TYPE_SCALE = [
  { token: '--text-display', label: 'Display — números-herói', sample: '78' },
  { token: '--text-title', label: 'Title — título de página', sample: 'Performance Score' },
  { token: '--text-section', label: 'Section — título de bloco', sample: 'Estudando agora' },
  {
    token: '--text-body-lg',
    label: 'Body LG — destaque de corpo',
    sample: 'Sessão de estudo com contexto.',
  },
  {
    token: '--text-body',
    label: 'Body — texto padrão',
    sample: 'O texto padrão da interface usa esta medida.',
  },
  { token: '--text-small', label: 'Small — apoio', sample: 'Informação de apoio e metadados.' },
  {
    token: '--text-micro',
    label: 'Micro — labels mono uppercase',
    sample: 'LABEL MONO · 2PX TRACKING',
  },
];

export function DesignScreen() {
  return (
    <div>
      <div class="main-head">
        <div>
          <h1 class="main-title">Design System</h1>
          <div class="main-sub">
            guia vivo · tokens + componentes · regras em docs/DESIGN_SYSTEM.md
          </div>
        </div>
        <div class="main-actions">
          <span class="tag gold">DEV</span>
        </div>
      </div>

      {/* ===== CORES ===== */}
      <div class="panel" style={{ marginBottom: '16px' }}>
        <div class="panel-title">
          ▰ <b>PALETA</b>
        </div>
        <div class="grid-4" style={{ marginBottom: '14px' }}>
          {COLORS.map((c) => (
            <div key={c.name} class="card">
              <div
                style={{
                  height: '44px',
                  borderRadius: 'var(--r-sm)',
                  background: `var(${c.name})`,
                  boxShadow: `0 0 14px ${c.hex}55`,
                  marginBottom: '8px',
                }}
              />
              <div class="mono" style={{ fontSize: 'var(--text-small)', color: 'var(--text)' }}>
                {c.name}
              </div>
              <div class="mono" style={{ fontSize: 'var(--text-micro)', color: 'var(--text-dim)' }}>
                {c.hex} · {c.use}
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap' }}>
          {TEXT_COLORS.map((t) => (
            <div key={t.name} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: `var(${t.name})`, fontSize: 'var(--text-body)' }}>Aa</span>
              <span
                class="mono"
                style={{ fontSize: 'var(--text-micro)', color: 'var(--text-dim)' }}
              >
                {t.name} — {t.use}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ===== TIPOGRAFIA ===== */}
      <div class="panel" style={{ marginBottom: '16px' }}>
        <div class="panel-title">
          ▰ <b>TIPOGRAFIA FLUIDA</b>
          <span class="mono" style={{ fontSize: 'var(--text-micro)' }}>
            redimensione a janela — tudo escala via clamp()
          </span>
        </div>
        {TYPE_SCALE.map((t) => (
          <div
            key={t.token}
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: '16px',
              padding: '10px 0',
              borderBottom: '1px dashed var(--border-soft)',
              flexWrap: 'wrap',
            }}
          >
            <span
              class="mono"
              style={{ fontSize: 'var(--text-micro)', color: 'var(--text-dim)', minWidth: '150px' }}
            >
              {t.token}
            </span>
            <span
              style={{
                fontSize: `var(${t.token})`,
                fontFamily:
                  t.token === '--text-micro'
                    ? 'var(--f-mono)'
                    : t.token === '--text-display' ||
                        t.token === '--text-title' ||
                        t.token === '--text-section'
                      ? 'var(--f-heading)'
                      : 'var(--f-body)',
                color: 'var(--text)',
                letterSpacing: t.token === '--text-micro' ? '2px' : undefined,
                fontWeight: t.token === '--text-display' ? 900 : undefined,
              }}
            >
              {t.sample}
            </span>
            <span class="mono" style={{ fontSize: 'var(--text-micro)', color: 'var(--text-mute)' }}>
              {t.label}
            </span>
          </div>
        ))}
        <div style={{ display: 'flex', gap: '20px', marginTop: '14px', flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'var(--f-heading)' }}>Orbitron — títulos e números</span>
          <span style={{ fontFamily: 'var(--f-body)' }}>Rajdhani — corpo de texto</span>
          <span style={{ fontFamily: 'var(--f-mono)' }}>JetBrains Mono — labels e dados</span>
        </div>
      </div>

      {/* ===== BOTÕES ===== */}
      <div class="panel" style={{ marginBottom: '16px' }}>
        <div class="panel-title">
          ▰ <b>BOTÕES</b>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
          <button class="btn primary">PRIMARY</button>
          <button class="btn">DEFAULT</button>
          <button class="btn ghost">GHOST</button>
          <button class="btn green">GREEN</button>
          <button class="btn gold">GOLD</button>
          <button class="btn pink">PINK</button>
          <button class="btn danger">DANGER</button>
          <button class="btn" disabled>
            DISABLED
          </button>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button class="btn primary sm">SM</button>
          <button class="btn primary">MD</button>
          <button class="btn primary lg">LG</button>
          <span class="mono" style={{ fontSize: 'var(--text-micro)', color: 'var(--text-dim)' }}>
            no mobile todos ganham min-height de toque (40-44px)
          </span>
        </div>
      </div>

      {/* ===== TAGS + KPI ===== */}
      <div class="split" style={{ marginBottom: '16px' }}>
        <div class="panel">
          <div class="panel-title">
            ▰ <b>TAGS</b>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <span class="tag">cyan</span>
            <span class="tag purple">purple</span>
            <span class="tag pink">pink</span>
            <span class="tag green">green</span>
            <span class="tag amber">amber</span>
            <span class="tag gold">gold</span>
            <span class="tag dim">dim</span>
          </div>
        </div>
        <div class="panel">
          <div class="panel-title">
            ▰ <b>KPI</b>
          </div>
          <div class="grid-3">
            <div class="kpi">
              <div class="label">Padrão</div>
              <div class="value">42</div>
              <div class="sub">subtexto</div>
            </div>
            <div class="kpi ok">
              <div class="label">Sucesso</div>
              <div class="value">87%</div>
              <div class="sub">verde</div>
            </div>
            <div class="kpi warn">
              <div class="label">Atenção</div>
              <div class="value">3d</div>
              <div class="sub">âmbar</div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== FORM + FEEDBACK ===== */}
      <div class="split" style={{ marginBottom: '16px' }}>
        <div class="panel">
          <div class="panel-title">
            ▰ <b>FORMULÁRIO</b>
          </div>
          <div class="field">
            <label>Input de texto</label>
            <input class="input" placeholder="digite algo..." />
          </div>
          <div class="field">
            <label>Select</label>
            <select class="select">
              <option>Opção A</option>
              <option>Opção B</option>
            </select>
          </div>
          <div class="field">
            <label>Textarea</label>
            <textarea class="textarea" placeholder="notas..." />
          </div>
        </div>
        <div class="panel">
          <div class="panel-title">
            ▰ <b>FEEDBACK</b>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
            <button class="btn sm" onClick={() => toast('Toast informativo', 'info')}>
              TOAST INFO
            </button>
            <button class="btn sm green" onClick={() => toast('✓ Deu certo!', 'ok')}>
              TOAST OK
            </button>
            <button class="btn sm" onClick={() => toast('⚠ Cuidado com isso', 'warn')}>
              TOAST WARN
            </button>
            <button class="btn sm danger" onClick={() => toast('✗ Algo falhou', 'err')}>
              TOAST ERR
            </button>
          </div>
          <div class="empty-state" style={{ padding: '20px 0' }}>
            <div class="ico">▦</div>
            <h3>Empty state</h3>
            <div>Assim mostramos telas sem dado.</div>
          </div>
        </div>
      </div>

      {/* ===== REGRAS ===== */}
      <div class="panel">
        <div class="panel-title">
          ▰ <b>REGRAS DE OURO</b>
        </div>
        <ul style={{ listStyle: 'none', fontSize: 'var(--text-body)', lineHeight: 2 }}>
          <li>▸ Tamanho de fonte NUNCA em px fixo — sempre token (--text-* ou --fs-*)</li>
          <li>▸ Dia/data comparados SEMPRE no fuso local (@core/dates)</li>
          <li>▸ Grid 2 colunas: classe .split (colapsa no mobile) — nunca inline</li>
          <li>▸ Tabela larga: envolver com .scroll-x + minWidth</li>
          <li>▸ Alvo de toque mínimo 44px no mobile (--touch)</li>
          <li>▸ z-index só da escala: --z-raise/drag/nav/sheet/modal/toast</li>
          <li>▸ Breakpoints: 640 (mobile) · 900 (sidebar) · 1280 (wide)</li>
        </ul>
      </div>
    </div>
  );
}
